import { useState } from 'react';
import { MessageSquare, Users, Calendar, Send } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TabMessage } from './tab-message';
import { TabRecipients } from './tab-recipients';
import { TabSchedule } from './tab-schedule';
import { TabDelivery } from './tab-delivery';
import { LivePreview } from './live-preview';
import { BroadcastSummary } from './broadcast-summary';
import { PreviewDialog } from './preview-dialog';
import type { useBroadcastForm } from '../hooks';

interface BroadcastFormLayoutProps {
  formId: string;
  form: ReturnType<typeof useBroadcastForm>;
  isPending: boolean;
  onSubmit: (e: React.FormEvent) => void;
}

export function BroadcastFormLayout({
  formId,
  form,
  isPending,
  onSubmit,
}: BroadcastFormLayoutProps) {
  const [activeTab, setActiveTab] = useState('message');

  return (
    <>
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
        {/* Левая колонка — форма */}
        <form id={formId} onSubmit={onSubmit}>
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="message" className="gap-2">
                <MessageSquare className="h-4 w-4" />
                <span className="hidden sm:inline">Сообщение</span>
                {form.hasMessageErrors && (
                  <span className="ml-1.5 h-2 w-2 rounded-full bg-destructive inline-block" />
                )}
              </TabsTrigger>
              <TabsTrigger value="recipients" className="gap-2">
                <Users className="h-4 w-4" />
                <span className="hidden sm:inline">Получатели</span>
              </TabsTrigger>
              <TabsTrigger value="delivery" className="gap-2">
                <Send className="h-4 w-4" />
                <span className="hidden sm:inline">Доставка</span>
              </TabsTrigger>
              <TabsTrigger value="schedule" className="gap-2">
                <Calendar className="h-4 w-4" />
                <span className="hidden sm:inline">Расписание</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="message" forceMount className="mt-6 data-[state=inactive]:hidden">
              <TabMessage
                name={form.name}
                setName={form.setName}
                messageTemplate={form.messageTemplate}
                setMessageTemplate={form.setMessageTemplate}
                parseMode={form.parseMode}
                setParseMode={form.setParseMode}
                channels={form.channels}
                toggleChannel={form.toggleChannel}
                hasChannels={form.hasChannels}
                availableVariables={form.availableVariables}
                isPending={isPending}
                languages={form.languages}
                translations={form.translations}
                activeTranslationTab={form.activeTranslationTab}
                setActiveTranslationTab={form.setActiveTranslationTab}
                updateTranslation={form.updateTranslation}
                emailVisualEnabled={form.emailVisualEnabled}
                setEmailVisualEnabled={form.setEmailVisualEnabled}
                emailDoc={form.emailDoc}
                onEmailEditorChange={form.handleEmailEditorChange}
                mediaEnabled={form.mediaEnabled}
                setMediaEnabled={form.setMediaEnabled}
                mediaType={form.mediaType}
                setMediaType={form.setMediaType}
                mediaUrl={form.mediaUrl}
                setMediaUrl={form.setMediaUrl}
                mediaCaption={form.mediaCaption}
                setMediaCaption={form.setMediaCaption}
                mediaParseMode={form.mediaParseMode}
                setMediaParseMode={form.setMediaParseMode}
                buttons={form.buttons}
                addButton={form.addButton}
                updateButton={form.updateButton}
                removeButton={form.removeButton}
                emailButtons={form.emailButtons}
                addEmailButton={form.addEmailButton}
                updateEmailButton={form.updateEmailButton}
                removeEmailButton={form.removeEmailButton}
                appButtons={form.appButtons}
                addAppButton={form.addAppButton}
                updateAppButton={form.updateAppButton}
                removeAppButton={form.removeAppButton}
              />
            </TabsContent>

            <TabsContent value="recipients" forceMount className="mt-6 data-[state=inactive]:hidden">
              <TabRecipients
                initialDefinition={form.recipientDataRef.current.definition}
                initialSelectedUserIds={form.recipientDataRef.current.selectedUserIds}
                onDataSync={form.handleRecipientDataChange}
                isPending={isPending}
              />
            </TabsContent>

            <TabsContent value="delivery" forceMount className="mt-6 data-[state=inactive]:hidden">
              <TabDelivery
                breakdown={form.breakdown}
                loading={form.breakdownLoading}
                error={form.breakdownError}
                onLoad={form.handleLoadBreakdown}
                isPending={isPending}
              />
            </TabsContent>

            <TabsContent value="schedule" forceMount className="mt-6 data-[state=inactive]:hidden">
              <TabSchedule
                scheduledAt={form.scheduledAt}
                setScheduledAt={form.setScheduledAt}
                isPending={isPending}
                languages={form.languages}
                translations={form.translations}
                messageTemplate={form.messageTemplate}
                botId={form.botId}
                setBotId={form.setBotId}
                channels={form.channels}
              />
            </TabsContent>
          </Tabs>
        </form>

        {/* Правая колонка — живой предпросмотр */}
        <aside className="hidden lg:block">
          <LivePreview
            channels={form.channels}
            languages={form.languages}
            previewLanguage={form.previewLanguage}
            setPreviewLanguage={form.setPreviewLanguage}
            loading={form.previewLoading}
            error={form.previewError}
            result={form.previewResult}
            payload={form.previewPayload}
            mediaUrl={form.mediaEnabled ? form.mediaUrl : undefined}
            onExpand={form.handleOpenPreview}
          />
        </aside>
      </div>

      {/* Сводка перед отправкой */}
      <BroadcastSummary
        channels={form.channels}
        name={form.name}
        messageTemplate={form.messageTemplate}
        scheduledAt={form.scheduledAt}
        result={form.previewResult}
        recipientCount={form.recipientCount}
      />

      <PreviewDialog
        open={form.previewOpen}
        onOpenChange={form.setPreviewOpen}
        languageLabel={form.previewLanguageLabel}
        loading={form.previewLoading}
        error={form.previewError}
        result={form.previewResult}
        channels={form.channels}
        payload={form.previewPayload}
        mediaUrl={form.mediaEnabled ? form.mediaUrl : undefined}
      />
    </>
  );
}
